import {
    TranslatableDisplayStrings as DisplayStrings,
    getAvailabilityDisplaySummary,
    getDisplayObjectHos,
    type IAvailabilityDisplayInfo,
    type IDisplayObjectHos
} from "hos-tools";
import { IHosProcessResult } from "./hosProcess";
import DutyStatusLog from "../model/DutyStatusLog";
import { OpenCabUserActions } from "../store/openCabUsers/actions";
import { getArrayCopy, getObjectCopy } from "../utils/vanilla";
import {
    ActionType,
    getCredentialsWithServerFromDriverSession,
    getDriverSessionArrayFromPayloadArray,
    IDriver,
    IDriverInfo,
    IDriverSession,
    IDriverSessionPayload,
    IHosClock,
    IHosStatus,
    IHosTeamOpenCabAvailability,
    IHosTeamOpenCabAvailabilityV0Dot4,
    IKeyTeamHosV0Dot4,
    instanceOfIDriverArray,
    instanceOfIDriverSessionPayloadArray,
    OpenCabDeepLink,
    ValueType
} from "../model/OpenCab";
import * as logger from "../utils/logger";
import { ICredentialsWithServer } from "../utils/credentialsStore";
import Context from "../app.context";
import { Router } from "../router";
import { UserService } from "../utils/users";
import { DriveStore } from "../store/reducers";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { observeStore } from "../utils/store";
import rison from "rison";
import { OpenCabWorkflowActions } from "../store/openCabWorkflow/actions";
import { getCurrentVehicle, isNoVehicle } from "../utils/device";
import { getServerDate } from "../utils/storage/date";
import { DAYS, HOURS } from "../constants";
import { ILoginRequiredState, LoginRequiredStatus } from "../store/mobile/types";
import { ServerFlags } from "../store/data/types";
import { DatabaseCache, IVersionInformation } from "./databaseCache";
import { BaseGeotabApi } from "../utils/api";
import AllowListProxy from "../utils/allowListProxy";
import { OpenCabQueueActions } from "../store/openCabQueue/actions";
import { ILoginUrlState } from "../ui/login";
import { TWorkflowStep } from "../ui/workflow";
import { OpenCabQueue, QueueActionType } from "../store/openCabQueue/types";
import { OpenCabTranslator } from "./openCabTranslator";

export const DISPLAY_CHECKMARK = "✓";

interface ICredentialsWithServerNext extends ICredentialsWithServer {
    next?: string;
}

export function getDefaultHosStatus (username?: string, clocks: IHosClock[] = []): IHosStatus {
    let result: IHosStatus = { clocks, manageAction: OpenCabDeepLink.MANAGE_ACTION, logoutAction: OpenCabDeepLink.LOGOUT_ACTION };
    if (username) {
        result.username = username;
    } else {
        result.username = "";
    }
    return result;
}

export function getDefaultTeamClocksResult (username?: string, clocks: IHosClock[] = []): IHosTeamOpenCabAvailability {
    return { KEY_HOS: getDefaultHosStatus(username, clocks), KEY_TEAM_HOS: [] };
}

export class OpenCabService {
    private readonly translator = new OpenCabTranslator(this.store, this.getDriverAndCoDrivers.bind(this), this.getContext.bind(this));

    constructor(
        private readonly store: DriveStore,
        private readonly userService: UserService,
        private readonly getContext: (userName?: string) => Context,
        private readonly router: Router
    ) {
        logger.log(logger.Source.OPENCAB, "Initializing the opencab service.");
        dispatcher.off(DispatcherEvents.OpenCab, undefined, this);
        dispatcher.on(DispatcherEvents.OpenCab, this.handleOpenCabEvent.bind(this), this);
        this.checkQueue();
    }

    checkQueue() {
        const maxQueueSize = 5;
        let q: OpenCabQueue[] = getArrayCopy(this.store.getState().openCabQueue);
        logger.log(logger.Source.OPENCAB, `Queue length ${ q.length }.`);
        if (q.length > maxQueueSize) {
            logger.error(logger.Source.OPENCAB, `Queue exceeded the limit. Received = ${ q.length }`);
            this.store.dispatch(OpenCabQueueActions.clearOpenCabQueue());
        }
        if (q.length > 0) {
            let unsubscribeFromPageName = observeStore(this.store, state => state.mobile.currentPage, state => {
                if (state === "ui/main") {
                    unsubscribeFromPageName();
                    let nextAction = q.shift();
                    this.store.dispatch(OpenCabQueueActions.dequeueOpenCabQueue());
                    logger.log(logger.Source.OPENCAB, `Navigating to ${ nextAction?.actionValue }.`);

                    switch (nextAction?.actionType) {
                        case QueueActionType.urlNavigation: {
                            nextAction && this.router.navigate(nextAction.actionValue, true);
                            break;
                        }
                        case QueueActionType.moveToBackground: {
                            this.moveAppToBackground();
                            break;
                        }
                        default: {
                            logger.error(logger.Source.OPENCAB, "Unknown queue action received");
                        }
                    }

                    this.checkQueue();
                }
            });
        }
    }

    checkOpenCabServiceAllowed(shouldThrow = true): boolean {
        return this.checkServerFlagEnabled(shouldThrow);
    }

    getHosTeamClocks = (requestVersion?: string) => {
        const currentVersion = "0.4";
        logger.log(logger.Source.OPENCAB, `Received a getHosTeamClocks call with version ${ requestVersion }.`);
        this.checkOpenCabServiceAllowed();
        switch (requestVersion) {
            case undefined:
            case "0.3":
                return this.getHosTeamClocksV0Dot3();
            case currentVersion:
                return this.getHosTeamClocksV0Dot4();
            default:
                throw new Error(`Version ${ requestVersion } is not supported. Latest supported version is ${ currentVersion }.`);
        }
    };

    getUsernameMap(): IIdDictionary<string> {
        this.checkOpenCabServiceAllowed();
        let unfilteredResult = getObjectCopy<IIdDictionary<string>>(this.store.getState().openCabUsers);
        this.deregisterInactiveUsernames(unfilteredResult);
        return getObjectCopy<IIdDictionary<string>>(this.store.getState().openCabUsers);
    }

    getBackgroundLoginUrl(driverSessions: IDriverSession[]): string {
        const getIsFirstDriver = (index: number) => index === 0 && !(Object.keys(getObjectCopy<IIdDictionary<string>>(this.store.getState().openCabUsers)).length > 0);

        const credentialsWithServerList: ICredentialsWithServerNext[] = driverSessions.map(driverSession => getCredentialsWithServerFromDriverSession(driverSession));
        let url = "";
        for (let i = credentialsWithServerList.length - 1; i >= 0; i--) {
            let credentialsWithServer = credentialsWithServerList[i];
            let { credentials, server } = credentialsWithServer;
            credentials = {...credentials, externalUsername: driverSessions[i].username};
            let isFirstDriver = getIsFirstDriver(i);
            let urlParams: ILoginUrlState = { credentials, server, skipWorkflow: isFirstDriver, addCoDriver: !isFirstDriver };
            if (credentialsWithServer.next) {
                urlParams.next = credentialsWithServer.next;
            }

            if (i > 0) {
                credentialsWithServerList[i - 1].next = `ui/login,${ rison.encode(urlParams) }`;
            } else {
                url = `ui/login,${ rison.encode(urlParams) }`;
            }
        }

        return url;
    }

    backgroundLoginDrivers(driverSessions: IDriverSession[]) {
        if (!driverSessions.length){
            return;
        }

        logger.event(logger.Event.DEBUG_LOGINVIEW_NAVIGATE, `OpenCab background login started for ${ driverSessions.length } users.`);
        window.location.hash = this.getBackgroundLoginUrl(driverSessions);
    }

    /**
     * Takes a list of external usernames for users who should be logged in and compares it to the users who are actually logged in to determine the actions required to sync the two lists.
     * @param activeDrivers is used to determine which users are meant to be logged in by their external username.
     * @returns Lists of external usernames for usersToKeep (the users which are logged in but need workflow), usersToRemove (users currently logged in who shouldn't be), and usersToAdd (users who are not logged in but should be).
     */
    syncLoggedInDrivers(activeDrivers: IDriver[]): { usersToKeep: string[]; usersToRemove: string[]; usersToAdd: string[] } {
        const usersMap = getObjectCopy<IIdDictionary<string>>(this.store.getState().openCabUsers);
        let currentUsers: string[] = [];
        for (let internalUsername in usersMap) {
            if (this.userService.getUserByName(internalUsername)) {
                currentUsers.push(usersMap[internalUsername]);
            }
        }
        const intendedUsers = activeDrivers.map(d => d.username.toLowerCase());
        !intendedUsers.length && this.store.dispatch(OpenCabWorkflowActions.clearPreTripDvirCompleteVehicleId());

        const usersToAdd = intendedUsers.filter(username => currentUsers.indexOf(username.toLowerCase()) === -1);
        const usersToRemove = currentUsers.filter(username => intendedUsers.indexOf(username.toLowerCase()) === -1);
        const usersToKeep = currentUsers.filter(username => intendedUsers.indexOf(username.toLowerCase()) > -1);

        logger.log(logger.Source.OPENCAB, `Sync result: usersToKeep=${ usersToKeep.length }, usersToRemove=${ usersToRemove.length }, usersToAdd=${ usersToAdd.length }`);

        return { usersToKeep, usersToRemove, usersToAdd };
    }

    startGuidedLogout(driversWhoNeedLogout: string[], callback?: () => void) {
        logger.log(logger.Source.OPENCAB, `Logout Action: driversWhoNeedLogout=${ driversWhoNeedLogout.length }`);
        if (driversWhoNeedLogout.length === 0) {
            callback && callback();
            return;
        }
        let invertedUsernameMap = this.getInvertedUsernameMap();
        let i = 0;
        let logoutUser = () => {
            logger.log(logger.Source.OPENCAB, `Logout user invoked: driversWhoNeedLogout=${ driversWhoNeedLogout.length }`);
            if (i < driversWhoNeedLogout.length) {
                logger.log(logger.Source.OPENCAB, `Logout Action: logging out`);
                let userName = invertedUsernameMap[driversWhoNeedLogout[i++].toLowerCase()];
                let userId = this.userService.getUserByName(userName)?.id;
                if (!userId) {
                    return;
                }
                if (this.userService.getActiveUserNames().length === 1) {
                    this.router.toWorkflow(true);
                } else {
                    this.router.navigateToPage("login", { switchUser: userId, next: `workflow,${ rison.encode({ isLogout: true, step: "checkPostTrip" }) }` });
                }
            }
        };
        let unsubscribeFromPageName = observeStore(this.store, state => state.mobile.currentPage, state => {
            if (state === "ui/main") {
                unsubscribeFromPageName();
                logoutUser();
            }
        });
        let unsubscribeFromLoginStatus = observeStore(this.store, state => state.mobile.loginRequired, (state: ILoginRequiredState) => {
            if (state.status === LoginRequiredStatus.LoginRequired) {
                unsubscribeFromLoginStatus();
                callback && callback();
                return;
            }
        });
        logoutUser();
    }

    startGuidedWorkflow(requiredWorkflowStepsByDriver: { [externalUsername: string]: TWorkflowStep }, callback?: () => void) {
        let driversWhoNeedWorkflowCount = Object.keys(requiredWorkflowStepsByDriver).length;
        logger.log(logger.Source.OPENCAB, `Handle Workflow started. Number of drivers=${ driversWhoNeedWorkflowCount }`);

        let nextWorkflowInfo = driversWhoNeedWorkflowCount && this.getNextWorkflowInfo(requiredWorkflowStepsByDriver);
        if (driversWhoNeedWorkflowCount === 0 || !nextWorkflowInfo) {
            callback && callback();
            return;
        }
        let { routeToWorkflowFunc } = nextWorkflowInfo;
        routeToWorkflowFunc();
        this.checkQueue();
    }

    registerOpenCabUser(internalUsername: string, externalUsername: string) {
        logger.event(logger.Event.OPENCABLOGIN, `${ internalUsername } logged in as ${ externalUsername }`);
        this.store.dispatch(OpenCabUserActions.setOpenCabUser({ internalUsername, externalUsername }));
    }

    private readonly handleFirstDeviceReadyEvent  = () => {
        if (window.geotabModules?.opencab) {
            logger.log(logger.Source.OPENCAB, "Running sync identities on startup.");
            this.syncIdentityInformation();
            document.removeEventListener("deviceready", this.handleFirstDeviceReadyEvent);
        } else {
            logger.error(logger.Source.OPENCAB, "Received 'deviceready' event but the OpenCab Native module does not exist.");
        }
    };

    private readonly clockBuilder = (
        label: string,
        value: string,
        durationMs: null | number = null,
        limitsDrivingRange = false,
        important = false,
        valueType: ValueType = ValueType.STRING
    ): IHosClock => {
        return { label, value, valueType, limitsDrivingRange, important, durationSeconds: durationMs === null ? null : durationMs / 1000 };
    };

    private getStatusClock(logs: DutyStatusLog[], username: string): IHosClock {
        let standardStatusLogs = logs.filter(hosLog => hosLog.isStandardStatusDriverLog());
        let latestStandardStatusLog = this.getContext(username).dutyStatusLogService.getLatestLog(standardStatusLogs);
        return this.clockBuilder(DisplayStrings.STATUS, latestStandardStatusLog?.status ?? "OFF", null, false, true, ValueType.STRING);
    }

    private getDisplayClocks(logs: DutyStatusLog[], username: string): { displayClocks: IHosClock[]; skipTranslateLabels: Set<string> } {
        const getClock = (availabilityDisplay: IAvailabilityDisplayInfo): IHosClock => {
            return this.clockBuilder(availabilityDisplay.label, availabilityDisplay.value, availabilityDisplay.ms, availabilityDisplay.limitsDrivingRange);
        };
        let ruleResult = this.getContext(username).hosProcess.getRuleResult(true, true);
        if (!ruleResult){
            return { displayClocks: [] as IHosClock[], skipTranslateLabels: new Set<string>() };
        }
        let displayObject = this.getDisplayObject(ruleResult, logs, username);
        if (!displayObject){
            return { displayClocks: [] as IHosClock[], skipTranslateLabels: new Set<string>() };
        }
        let availabilityDisplaySummary = getAvailabilityDisplaySummary(displayObject, ruleResult.preciseAvailability);
        let translator = this.getContext(username).translationService.getTranslator();
        availabilityDisplaySummary.forEach(item => {
            if (!item.skipTranslateLabel) {
                item.label = this.translator.translate(item.label, translator);
            }
        });
        let displayClocks = availabilityDisplaySummary.map(a => getClock(a));
        return { displayClocks, skipTranslateLabels: new Set(availabilityDisplaySummary.map(a => a.label)) };
    }

    private getDisplayObject(ruleResult: IHosProcessResult, logs: DutyStatusLog[], username: string): IDisplayObjectHos | undefined {
        let context = this.getContext(username);
        let latestRuleSet = context.hosProcess.getUserHosRuleSets().getRulesetByDate(new Date());
        let ruleSetKey = latestRuleSet?.hosRuleSet?.id;
        if (!ruleResult || !ruleSetKey) {
            return;
        }
        let translator = context.translationService.getTranslator();
        let currentLog = this.getDriverContext().dutyStatusLogService.getLatestLog(logs); // todo: should this be for the requested user instead of driver?
        let daysString = this.translator.translate(DisplayStrings.DAYS_STRING, translator);
        let translatedHourUnit = this.translator.translate(DisplayStrings.HOUR_UNIT, translator) ?? DisplayStrings.HOUR_UNIT;
        let translatedMinuteUnit = this.translator.translate(DisplayStrings.MINUTES_STRING, translator).replace("{minutes}", "") ?? DisplayStrings.HOUR_UNIT;
        let translatedDrivingBreakDuration = this.translator.translate("{0} Break duration", translator);
        let translatedDrivingBreak = this.translator.translate("{0} Break", translator);
        let translatedDrivingBreakIn = this.translator.translate("{0} Break required in", translator);
        return getDisplayObjectHos(
            ruleResult,
            ruleSetKey,
            currentLog,
            daysString,
            translatedHourUnit,
            translatedMinuteUnit,
            translatedDrivingBreakDuration,
            translatedDrivingBreak,
            translatedDrivingBreakIn,
            DISPLAY_CHECKMARK
        );
    }

    private getUsernameClock(username: string): IHosClock {
        return this.clockBuilder(DisplayStrings.USER, username);
    }

    private getClocksForDriver(externalUsername: string, internalUsername: string) {
        let user = this.userService.getUserByName(internalUsername);
        let context = this.getContext(user?.name);
        let logs = context.dutyStatusLogService.getHOSLogs(false, user?.id);
        let statusClock = this.getStatusClock(logs, internalUsername);
        let { displayClocks, skipTranslateLabels } = this.getDisplayClocks(logs, internalUsername);
        let userNameClock = this.getUsernameClock(externalUsername);

        // translations
        let translator = context.translationService.getTranslator();
        [userNameClock, statusClock, ...displayClocks].forEach(c => !skipTranslateLabels.has(c.label) && (c.label = this.translator.translate(c.label, translator)));
        if (userNameClock.value === DisplayStrings.Unknown) {
            userNameClock.value = this.translator.translate(DisplayStrings.Unknown, translator);
        }

        return { userNameClock, statusClock, displayClocks };
    }

    private getHosStatus (username: string, clocks: IHosClock[]): IHosStatus {
        return { username, clocks, manageAction: OpenCabDeepLink.MANAGE_ACTION, logoutAction: OpenCabDeepLink.LOGOUT_ACTION };
    }

    private getDriverContext(): Context {
        return this.getContext(this.userService.getDriversSeatUser()?.name);
    }

    private deregisterInactiveUsernames(usernameMap: IIdDictionary<string>) {
        let activeInternalUsernames = this.userService.getActiveUserNames();
        Object.keys(usernameMap).forEach(internalUsername =>
            activeInternalUsernames.indexOf(internalUsername) === -1 && this.store.dispatch(OpenCabUserActions.clearOpenCabUser(internalUsername)));
    }

    private checkServerFlagEnabled(shouldThrow = true, serverFlag: ServerFlags = this.store.getState().data.serverFlags): boolean {
        const isServerFlagEnabled = serverFlag !== ServerFlags.None;
        if (!isServerFlagEnabled && shouldThrow) {
            throw new Error("The target server doesn't allow OpenCab.");
        }
        return isServerFlagEnabled;
    }

    private getInvertedUsernameMap(): IIdDictionary<string> {
        let usernameMap = this.getUsernameMap();
        let invertedUsernameMap: IIdDictionary<string> = {};
        for (let user in usernameMap) {
            invertedUsernameMap[usernameMap[user]] = user;
        }
        return invertedUsernameMap;
    }

    /**
     * Takes credentials for a server and check if that server allows OpenCab, without adding the user to DriveApp.
     * Does not mutate the app state until the server is confirmed to support OpenCab.
     * @param credentialsWithServer is the credentials to use for the API calls to the server.
     * @private
     */
    private checkOpenCabLoginAllowedForIncomingCredentials(credentialsWithServer: ICredentialsWithServer): Promise<void> {
        if (this.userService.getCurrentUser()) { // if somebody is already logged in, we don't need to check the server again
            this.checkOpenCabServiceAllowed();
            return Promise.resolve();
        }

        return BaseGeotabApi.fetchWithoutSideEffects<IVersionInformation>(credentialsWithServer.server, "GetVersionInformation", {}, true)
            .then(versionInformation => {
                if (!versionInformation) {
                    throw new Error("Could not verify if the target server allows OpenCab.");
                }
                this.checkServerFlagEnabled(true, versionInformation.serverFlags);
                logger.log(logger.Source.OPENCAB, `Login attempt passed ServerFlag check.`);
                return versionInformation;
            }).then(versionInformation => {
                // since feature is allowed for target server, we can actually set the info now
                versionInformation && DatabaseCache.setVersionInformation(this.store, versionInformation);
            })
            .catch(error => logger.error(logger.Source.OPENCAB, `checkOpenCabLoginAllowedForIncomingCredentials error: ${ error.message }`));
    }


    private validateAndFilterDriverSessionsPayload(driverSessionsPayload?: IDriverSessionPayload[]): IDriverSession[] {
        if (!driverSessionsPayload) {
            logger.error(logger.Source.OPENCAB, "getLoginCredentials returned undefined.");
            throw new Error("Invalid result format");
        }
        if (!instanceOfIDriverSessionPayloadArray(driverSessionsPayload)) {
            logger.error(logger.Source.OPENCAB, "getLoginCredentials returned an object that did not match the expected interface.");
            throw new Error("Invalid result format");
        }
        logger.log(logger.Source.OPENCAB, `Received ${ driverSessionsPayload.length } credentials for drivers.`);
        // if there is an active user, filter out any incoming credentials that point to a different server address
        let currentUser = this.userService.getCurrentUser();
        let currentServer = currentUser ? this.store.getState().driveAPICredentials?.[currentUser.name]?.server : undefined;
        let driverSessions = this.validateAndFilterDriverSessions(getDriverSessionArrayFromPayloadArray(driverSessionsPayload), currentServer);
        if (driverSessions.length === 0) {
            throw new Error("LoginCredentials included 0 driver sessions matching the active user's server.");
        }
        return driverSessions;
    }

    private getLoginCredentials(callback: (result: IDriverSession[]) => void): void {
        const openCabGeotabModule = window.geotabModules?.opencab;
        if (!openCabGeotabModule) {
            throw new Error("OpenCab Native module is not available.");
        }
        logger.log(logger.Source.OPENCAB, "Requesting login credentials.");
        openCabGeotabModule.getLoginCredentials(undefined, (error?: Error, result?: IDriverSessionPayload[]) => {
            if (error) {
                logger.error(logger.Source.OPENCAB, `getLoginCredentials error: ${ error.message }`);
                return;
            }
            let driverSessions = this.validateAndFilterDriverSessionsPayload(result);
            this.checkOpenCabLoginAllowedForIncomingCredentials(getCredentialsWithServerFromDriverSession(driverSessions[0]))
                .then(() => callback(driverSessions))
                .catch(e => logger.error(logger.Source.OPENCAB, `getLoginCredentials error: ${ e.message }`));
        });
    }

    private getActiveDrivers(callback: (result: IDriver[]) => void): void {
        const openCabGeotabModule = window.geotabModules?.opencab;
        if (!openCabGeotabModule) {
            logger.error(logger.Source.OPENCAB, "Tried calling getActiveDrivers but OpenCab Native module is not available.");
            return;
        }
        logger.log(logger.Source.OPENCAB, "Requesting active drivers.");
        openCabGeotabModule.getActiveDrivers(undefined, (error?: Error, result?: IDriver[]) => {
            if (error) {
                logger.error(logger.Source.OPENCAB, `getActiveDrivers error: ${ error.message }`);
                throw error;
            }
            if (result) {
                if (instanceOfIDriverArray(result)) {
                    logger.log(logger.Source.OPENCAB, `Received ${ result.length } active drivers.`);
                    result.forEach(driver => driver.username = driver.username.toLowerCase());
                    callback(result);
                } else {
                    logger.error(logger.Source.OPENCAB, "getActiveDrivers returned an object that did not match the expected interface.");
                    throw new Error("Invalid result format");
                }
            } else {
                logger.error(logger.Source.OPENCAB, "getActiveDrivers returned undefined.");
            }
        });
    }

    private syncIdentityInformation() {
        this.getActiveDrivers((activeDrivers) => {
            if (activeDrivers.length > 0) {
                let userSync = this.checkOpenCabServiceAllowed(false) ? this.syncLoggedInDrivers(activeDrivers) : undefined;
                let usersNeedingLogin = userSync ? userSync.usersToAdd : activeDrivers.map(d => d.username);
                let usersLoggedInButMissingCredentials: string[] = userSync ? this.getUsersMissingCredentials(userSync.usersToKeep) : [];
                let usersToLogin = [...usersNeedingLogin, ...usersLoggedInButMissingCredentials];

                this.getLoginCredentials(driverSessions => {
                    let refreshedUsers = this.attemptSessionRefresh(activeDrivers, driverSessions);
                    let usersToBackgroundLogin = driverSessions.filter(s => usersToLogin.indexOf(s.username) > -1 && !refreshedUsers[s.username]);
                    if (usersToBackgroundLogin.length) {
                        logger.log(
                            logger.Source.OPENCAB,
                            `Starting background logins for ${ usersToBackgroundLogin.length } users after refreshing ${ Object.keys(refreshedUsers).length } sessions.`
                        );
                        this.backgroundLoginDrivers(usersToBackgroundLogin);
                    }
                });
            }
        });
    }

    private attemptSessionRefresh (activeDrivers: IDriver[], driverSessions: IDriverSession[]): { [externalUsername: string]: true } {
        if (driverSessions.length !== activeDrivers.length) {
            logger.error(logger.Source.OPENCAB, `Received ${ driverSessions.length } driver sessions for ${ activeDrivers.length } active drivers.`);
        }
        let activeDriverExternalNames = activeDrivers.map(d => d.username);
        let newCredentials = driverSessions.filter(s => activeDriverExternalNames.indexOf(s.username) > -1); // n^2 but n is at most 2
        let invertedUsernameMap = this.getInvertedUsernameMap();
        let refreshedUsers: { [externalUsername: string]: true } = {};
        newCredentials.forEach(newCredential => {
            let internalUsername = invertedUsernameMap[newCredential.username];
            let userContext = this.getContext(internalUsername);
            let currentCredential = userContext.api.getDriverCredentials(internalUsername);
            if (
                currentCredential !== null &&
                newCredential.loginCredentials.token.path.toLowerCase() === currentCredential?.server.toLowerCase() &&
                newCredential.loginCredentials.token.credentials.userName.toLowerCase() === currentCredential?.credentials.userName.toLowerCase() &&
                newCredential.loginCredentials.token.credentials.database.toLowerCase() === currentCredential?.credentials.database.toLowerCase() &&
                newCredential.loginCredentials.token.credentials.sessionId !== currentCredential?.credentials.sessionId) {
                logger.log(logger.Source.OPENCAB, `Refreshing sessionId for ${ internalUsername }.`);
                let newCredentialsWithServer: ICredentialsWithServer = {
                    server: currentCredential.server,
                    credentials: {
                        ...currentCredential.credentials,
                        sessionId: newCredential.loginCredentials.token.credentials.sessionId
                    }
                };
                userContext.api.setUser(newCredentialsWithServer.credentials, newCredentialsWithServer.server);
                refreshedUsers[newCredential.username] = true;
            }
        });
        if (Object.keys(refreshedUsers).length === 0) {
            logger.log(logger.Source.OPENCAB, "No sessions were refreshed.");
        }
        return refreshedUsers;
    }

    private getUsersMissingCredentials (loggedInExternalUsernames: string[]): string[] {
        let invertedUsernameMap = this.getInvertedUsernameMap();
        let usersNeedingSessionRefresh: string[] = loggedInExternalUsernames.filter(username => {
            let internalUsername = invertedUsernameMap[username];
            return !!internalUsername && !this.store.getState().driveAPICredentials[internalUsername];
        });
        if (usersNeedingSessionRefresh.length > 0) {
            logger.log(logger.Source.OPENCAB, `Found ${ usersNeedingSessionRefresh.length } users missing credentials.`);
        }
        return usersNeedingSessionRefresh;
    }

    private getDriverAndCoDrivers(): { driver?: DeepReadonly<IUser>; coDrivers: DeepReadonly<IUser>[] } {
        let driver = this.userService.getDriversSeatUser();
        if (!driver?.name) {
            return { coDrivers: [] };
        }
        let coDriverUsernames = this.userService.getActiveUserNames().filter(u => u !== driver?.name);
        const usersDict = this.userService.getUsersObject();
        let coDrivers = coDriverUsernames.map(c => usersDict[c]);
        return { driver, coDrivers };
    }

    private handleManageActionEvent () {
        this.getActiveDrivers(activeDrivers => {
            let usersToKeep = this.syncLoggedInDrivers(activeDrivers).usersToKeep;
            let requiredWorkflowStepsByDriver = this.getRequiredWorkflowStepsByDriver();
            Object.keys(requiredWorkflowStepsByDriver).forEach(externalUsername => {
                if (usersToKeep.indexOf(externalUsername) === -1) {
                    delete requiredWorkflowStepsByDriver[externalUsername];
                }
            });
            this.startManageActionSteps(requiredWorkflowStepsByDriver);
        });
    }

    private startManageActionSteps (requiredWorkflowStepsByDriver: { [externalUsername: string]: TWorkflowStep }) {
        if (Object.keys(requiredWorkflowStepsByDriver).length === 0) {
            return;
        }
        this.startGuidedWorkflow(requiredWorkflowStepsByDriver, () => {
            this.moveAppToBackground();
        });
    }

    private handleLogoutActionEvent () {
        this.startGuidedLogout(Object.keys(this.getInvertedUsernameMap()), this.moveAppToBackground.bind(this));
    }

    private handleOpenCabEvent (action: number) {
        logger.log(logger.Source.OPENCAB, `Received event ${ ActionType[action].toString() }.`);
        switch (action) {
            case ActionType.MANAGE_ACTION:
                this.handleManageActionEvent();
                break;
            case ActionType.LOGOUT_ACTION:
                this.handleLogoutActionEvent();
                break;
            case ActionType.ACTION_DRIVER_LOGIN:
            case ActionType.ACTION_DRIVER_LOGOUT:
            case ActionType.ACTION_IDENTITY_INFORMATION_CHANGED:
                this.syncIdentityInformation();
                break;
            case ActionType.DEFAULT:
            default:
                logger.log(logger.Source.OPENCAB, "Default event action encountered.");
        }
    }

    private validateAndFilterDriverSessions (driverSessions: IDriverSession[], currentServer?: string): IDriverSession[] {
        if (!currentServer) {
            return driverSessions;
        }
        let validSessions = driverSessions.filter(loginRequest => loginRequest.loginCredentials.token.path === currentServer);
        let filteredCount = driverSessions.length - validSessions.length;
        filteredCount > 0
            && logger.log(logger.Source.OPENCAB, `${ filteredCount } incoming login requests were discarded because they didn't match the current user's server.`);
        return validSessions;
    }

    private didUserSkipUnverifiedLogsInLast24h (userId: string): boolean {
        const userSkippedUnverifiedLogs = this.store.getState().openCabWorkflow.userSkippedUnverifiedLogsDate;
        if (!userSkippedUnverifiedLogs) {
            return false;
        }
        const userSkipDate = new Date(userSkippedUnverifiedLogs[userId]);
        return userSkipDate.getTime() + (24 * HOURS) >= getServerDate().getTime();
    }

    private getRequiredWorkflowStep(driverInfo: IDriverInfo): TWorkflowStep | undefined {
        let driverContext = driverInfo.context;
        let user = driverContext.api.getUser();
        let isDriverSeatUser = this.isDriverSeatUser(user);
        // If a vehicle has not been selected or a pretrip dvir is needed then always return true
        if (isDriverSeatUser) {
            if (this.isAssetSelectionRequired()) {
                return "selectVehicle";
            }
        }

        const isVerifyLogsWorkflowRequired = (): boolean => {
            if (user?.id && this.didUserSkipUnverifiedLogsInLast24h(user?.id)) {
                return false; // if the user skipped verifying their logs during workflow, do not ask them to verify logs again for 24h.
            }
            const endDate = new Date(getServerDate().getTime() - DAYS);
            return driverContext.securityService.canVerifyLogsForWorkflow(false) &&
                driverContext.dutyStatusLogService.isAnyLogUnverifiedForWorkflow(driverContext.hosProcess, false, driverInfo.user, endDate);
        };

        if (isVerifyLogsWorkflowRequired()) {
            return "verifyLogs";
        }

        return undefined;
    }

    private isAssetSelectionRequired (): boolean {
        const currentVehicle = getCurrentVehicle();
        if (this.store.getState().openCabWorkflow.skippedAssetSelection) {
            !isNoVehicle() && this.store.dispatch(OpenCabWorkflowActions.setSkippedAssetSelection(false));
            if (currentVehicle?.id === "NoDeviceId") {
                return true;
            }
        }
        return currentVehicle === null;
    }

    private getRequiredWorkflowStepsByDriver (): { [externalUsername: string]: TWorkflowStep } {
        let { driver, coDrivers } = this.getDriverAndCoDrivers();
        if (!driver) {
            return {};
        }
        let userNameMap = this.getUsernameMap();
        let driverInfos: IDriverInfo[] = [driver, ...coDrivers].map(user => {
            return { user, externalUsername: userNameMap[user?.name], context: this.getContext(user?.name) };
        });
        let requiredWorkflowStepsByDriver: { [externalUsername: string]: TWorkflowStep} = {};
        driverInfos.forEach(driverInfo => {
            const requiredWorkflowStep = driverInfo.externalUsername !== undefined ? this.getRequiredWorkflowStep(driverInfo) : undefined;
            if (requiredWorkflowStep && driverInfo.externalUsername) {
                requiredWorkflowStepsByDriver[driverInfo.externalUsername] = requiredWorkflowStep;
            }
        });
        return requiredWorkflowStepsByDriver;
    }

    private translateStringsForDriverSeatDriver (strings: string[]): string[] {
        let { driver } = this.getDriverAndCoDrivers();
        if (!driver) {
            return strings;
        }
        let translator = this.getContext(driver.name).translationService.getTranslator();
        return strings.map(s => this.translator.translate(s, translator));
    }

    private getTapToLoginResult (): IHosTeamOpenCabAvailability {
        let hosTeamContractClocks: IHosTeamOpenCabAvailability = getDefaultTeamClocksResult();
        let [label, value] = this.translateStringsForDriverSeatDriver([DisplayStrings.DRIVE_APP_HOURS_OF_SERVICE, DisplayStrings.TAP_TO_LOGIN]);
        hosTeamContractClocks.KEY_HOS.clocks.push(this.clockBuilder(label, value, null, false, true));
        return hosTeamContractClocks;
    }

    private getTapToCompleteWorkflowResult (): IHosTeamOpenCabAvailability {
        let hosTeamContractClocks: IHosTeamOpenCabAvailability = getDefaultTeamClocksResult();
        let [label, value] = this.translateStringsForDriverSeatDriver([DisplayStrings.COMPLIANCE, DisplayStrings.TAP_TO_COMPLETE_WORKFLOW]);
        hosTeamContractClocks.KEY_HOS.clocks.push(this.clockBuilder(label, value, null, false, true));
        return hosTeamContractClocks;
    }

    private moveAppToBackground () {
        logger.log(logger.Source.OPENCAB, `Moving app to background.`);
        const appModule = window.geotabModules?.app;
        if (!appModule) {
            logger.error(logger.Source.OPENCAB, "App Native module is not available to move app to background.");
            return;
        }

        appModule.moveAppToBackground(undefined, (err?: Error) => err && logger.error(logger.Source.OPENCAB, `moveAppToBackground error: ${ err.message }`));
    }
    private getNextWorkflowInfo(requiredWorkflowStepsByDriver: { [externalUsername: string]: TWorkflowStep }): { routeToWorkflowFunc: () => void } | undefined {
        let usernameMap = this.getUsernameMap();
        let invertedUsernameMap = this.getInvertedUsernameMap();
        let currentUser = this.userService.getCurrentUser();
        if (!currentUser?.name) {
            return;
        }

        let driversWhoNeedWorkflow = Object.keys(requiredWorkflowStepsByDriver);
        let otherUserExternalUsername = driversWhoNeedWorkflow.find(u => u !== usernameMap[currentUser?.name ?? ""]) ?? "";
        let otherUser = otherUserExternalUsername ? this.userService.getUserByName(invertedUsernameMap[otherUserExternalUsername]) : null;

        let doesCurrentUserNeedWorkflow = driversWhoNeedWorkflow.indexOf(usernameMap[currentUser.name]) > -1;
        let isDriverSeatUser = this.isDriverSeatUser(currentUser);

        if (driversWhoNeedWorkflow.length === 0) {
            return;
        } else if (driversWhoNeedWorkflow.length === 1) {
            if (doesCurrentUserNeedWorkflow) {
                // case 1: current user needs workflow only
                this.store.dispatch(OpenCabQueueActions.enqueueOpenCabQueue({actionValue: "moveAppToBackground", actionType: QueueActionType.moveToBackground}));
                let currentUserExternalUsername = usernameMap[currentUser.name];
                return {
                    routeToWorkflowFunc: () => this.router.navigate(`ui/workflow,${ rison.encode({ step: requiredWorkflowStepsByDriver[currentUserExternalUsername] }) }`, true)
                };
            } else {
                // case 2: other user needs workflow only
                // first, switch to the other user (driver) and start the workflow
                let nextUrl = `ui/login,${ rison.encode({switchUser: otherUser?.id, next: `ui/workflow,${ rison.encode({step: requiredWorkflowStepsByDriver[otherUserExternalUsername]}) }`}) }`;
                // second, switch back to the current user and background the app
                let currentUserUrl = `ui/login,${ rison.encode({switchUser: currentUser.id, next: "ui/main"}) }`;
                this.store.dispatch(OpenCabQueueActions.enqueueOpenCabQueue({ actionType: QueueActionType.urlNavigation, actionValue: currentUserUrl }));
                logger.log(logger.Source.OPENCAB, `Case 2 - Queued ${ currentUserUrl }.`);
                // lastly, move the app to the background
                this.store.dispatch(OpenCabQueueActions.enqueueOpenCabQueue({actionType: QueueActionType.moveToBackground, actionValue: "moveAppToBackground"}));
                // return the start of the process.
                return { routeToWorkflowFunc: () => this.router.navigate(nextUrl, true) };
            }
        } else if (driversWhoNeedWorkflow.length === 2) {
            if (isDriverSeatUser) {
                // case 3: both need workflow and current user is main driver
                let currentUserExternalUsername = usernameMap[currentUser.name];
                let currentUserWorkflowStep = requiredWorkflowStepsByDriver[currentUserExternalUsername];
                let otherUserWorkflowStep = requiredWorkflowStepsByDriver[otherUserExternalUsername];
                // first, take the current user through their workflow
                let nextUrl = `ui/workflow,${ rison.encode({ step: currentUserWorkflowStep }) }`;
                // second, switch to the other user (driver) and start the workflow
                let otherUserUrl = `ui/login,${ rison.encode({switchUser: otherUser?.id, next: `ui/workflow,${ rison.encode({coDriver: true, step: otherUserWorkflowStep}) }`}) }`;
                this.store.dispatch(OpenCabQueueActions.enqueueOpenCabQueue({ actionValue: otherUserUrl, actionType: QueueActionType.urlNavigation }));
                // lastly, switch back to the currentUser and background the app
                let switchBackUrl = `ui/login,${ rison.encode({ switchUser: currentUser.id }) }`;
                this.store.dispatch(OpenCabQueueActions.enqueueOpenCabQueue({actionValue: switchBackUrl, actionType: QueueActionType.urlNavigation}));
                this.store.dispatch(OpenCabQueueActions.enqueueOpenCabQueue({actionValue: "moveAppToBackground", actionType: QueueActionType.moveToBackground}));
                logger.log(logger.Source.OPENCAB, `Case 3 - Queued ${ otherUserUrl } ${ switchBackUrl }.`);
                return { routeToWorkflowFunc: () => this.router.navigate(nextUrl, true) };
            } else {
                // case 4: both need workflow and current user is not the main driver
                let currentUserExternalUsername = usernameMap[currentUser.name];
                let currentUserWorkflowStep = requiredWorkflowStepsByDriver[currentUserExternalUsername];
                let otherUserWorkflowStep = requiredWorkflowStepsByDriver[otherUserExternalUsername];
                // first, switch to the other user (driver) and start the workflow
                let nextUrl = `ui/login,${ rison.encode({switchUser: otherUser?.id, next: `ui/workflow,${ rison.encode({step: otherUserWorkflowStep}) }`}) }`;
                // second, switch back to the current user and start the workflow
                let currentUserUrl = `ui/login,${ rison.encode({switchUser: currentUser.id, next: `ui/workflow,${ rison.encode({step: currentUserWorkflowStep}) }`}) }`;
                this.store.dispatch(OpenCabQueueActions.enqueueOpenCabQueue({ actionType: QueueActionType.urlNavigation, actionValue: currentUserUrl }));
                logger.log(logger.Source.OPENCAB, `Case 4 - Queued ${ currentUserUrl }.`);
                // lastly, move the app to the background
                this.store.dispatch(OpenCabQueueActions.enqueueOpenCabQueue({actionType: QueueActionType.moveToBackground, actionValue: "moveAppToBackground"}));
                // return the start of the process.
                return { routeToWorkflowFunc: () => this.router.navigate(nextUrl, true) };
            }
        }

        if (driversWhoNeedWorkflow.length > 2) {
            logger.error(logger.Source.OPENCAB, `driversWhoNeedWorkflow exceeded the limit. Received = ${ driversWhoNeedWorkflow.length }`);
        }
        return undefined;
    }

    private isDriverSeatUser(currentUser: DeepReadonlyObject<IUser> | null) {
        return this.userService.getDriversSeatUser()?.name === currentUser?.name;
    }

    private getHosTeamClocksV0Dot3() {
        let { driver, coDrivers } = this.getDriverAndCoDrivers();
        if (!driver) {
            return this.getTapToLoginResult();
        }
        const usersMap = this.getUsernameMap();
        let usersToRefresh = this.userService.getActiveUserNames().filter(internalUsername => !!usersMap[internalUsername] && !this.store.getState().driveAPICredentials[internalUsername]);
        if (usersToRefresh.length > 0) {
            this.syncIdentityInformation();
        }

        if ((driver || coDrivers.length) && Object.keys(usersMap).length === 0) {
            return this.getTapToLoginResult();
        }
        const requiredWorkflowSteps = Object.keys(usersMap).length ? this.getRequiredWorkflowStepsByDriver() : null;
        if (requiredWorkflowSteps && Object.keys(requiredWorkflowSteps).length) {
            return this.getTapToCompleteWorkflowResult();
        }

        const getHosStatusForUser = (user: DeepReadonlyObject<IUser>): IHosStatus => {
            const internalUsername = user.name;
            const externalUsername = usersMap.hasOwnProperty(internalUsername) ? usersMap[internalUsername] : DisplayStrings.Unknown;
            const clocks = this.getClocksForDriver(externalUsername, internalUsername);
            return this.getHosStatus(externalUsername, [clocks.userNameClock, clocks.statusClock, ...clocks.displayClocks]);
        };
        const hosTeamContractClocks: IHosTeamOpenCabAvailability = getDefaultTeamClocksResult();
        hosTeamContractClocks.KEY_HOS = getHosStatusForUser(driver);
        hosTeamContractClocks.KEY_TEAM_HOS = coDrivers.map(getHosStatusForUser);
        return hosTeamContractClocks;
    }

    private getHosTeamClocksV0Dot4() {
        const convertToHosTeamOpenCabAvailabilityV0Dot4 = (hosTeamOpenCabAvailability: IHosTeamOpenCabAvailability) => {
            const keyHosTeamV0Dot4: IKeyTeamHosV0Dot4 = {
                teamHosData: hosTeamOpenCabAvailability.KEY_TEAM_HOS
            };
            return {
                KEY_HOS: JSON.stringify(hosTeamOpenCabAvailability.KEY_HOS),
                KEY_TEAM_HOS: JSON.stringify(keyHosTeamV0Dot4)
            } as IHosTeamOpenCabAvailabilityV0Dot4;
        };
        return convertToHosTeamOpenCabAvailabilityV0Dot4(this.getHosTeamClocksV0Dot3());
    }
}

export const getProxiedOpenCabService = (...args: ConstructorParameters<typeof OpenCabService>) => {
    /* These functions are allowListed since they are required for the first OpenCab login to succeed.
    It is important that they do not modify the internal state of DriveApp until it is verified that the request is for a server that enables and allows OpenCab. */
    const alwaysAllowedFunctions: { [functionName: string]: true } = {
        "handleOpenCabEvent": true,
        "syncIdentityInformation": true,
        "getActiveDrivers": true,
        "getLoginCredentials": true,
        "validateAndFilterDriverSessions": true,
        "validateAndFilterDriverSessionsPayload": true,
        "checkOpenCabLoginAllowedForIncomingCredentials": true,
        "checkServerFlagEnabled": true,
        "checkOpenCabServiceAllowed": true
    };
    return new AllowListProxy<OpenCabService>(
        new OpenCabService(...args),
        alwaysAllowedFunctions,
        openCabService => !!openCabService?.checkOpenCabServiceAllowed()
    ).getProxiedTarget();
};
